// Display helpers of the Licenses page (License Insights, spec 2026-10-25). Pure, no React.

/** "$8,188" (whole units, KPIs) or "$39.00" (cents, table and export). Null or not finite: null (the caller shows a dash). */
export function formatMoney(amount, currency = 'USD', { cents = false } = {}) {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0,
    }).format(amount);
  } catch {
    return `${amount.toFixed(cents ? 2 : 0)} ${currency}`;
  }
}

/** "1,595" */
export const formatCount = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('en-US') : '0');

/** "38 seats", "1 seat", "6 people", "1 person". */
export function countWords(n, one, many) {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

/** The right side of a finding row: money per month when known, then the seats or people in words. */
export function impactWords(finding, currency) {
  const money = formatMoney(finding.impact.monthly, currency);
  const units = finding.id === 'LIC-005' || finding.id === 'LIC-006' || finding.id === 'LIC-007'
    ? countWords(finding.impact.people, 'person', 'people')
    : countWords(finding.impact.seats, 'seat', 'seats');
  return { money: money ? `${money}/mo` : null, units };
}

/** What the price cell says under the amount. */
export function priceSourceWords(price) {
  if (!price) return null;
  if (price.source === 'entered') return 'Your price';
  if (price.source === 'estimate') return 'Estimate';
  if (price.source === 'free') return 'Free';
  return null;
}

/** The yearly caption of the savings KPI, naming whose prices it uses. */
export function savingsCaption(kpis, prices, currency) {
  if (kpis.monthly === null) return 'Add prices to see savings';
  const yearly = formatMoney(kpis.yearly, currency);
  if (prices.estimated > 0 && prices.entered > 0) return `${yearly} a year, at your prices and Microsoft list price estimates`;
  if (prices.estimated > 0) return `${yearly} a year, at Microsoft list price estimates`;
  return `${yearly} a year, at your prices`;
}

/** A percent for the KPI bar, or null. */
export const share = (part, whole) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : null);

/** The amount typed in a price field as a number, or NaN ("", "abc"). Accepts "36", "31.50", "$31.50", "1,200". */
export function parsePriceText(text) {
  const t = String(text ?? '').trim().replace(/^[$€£]/, '').replace(/,/g, '');
  if (t === '' || !/^\d+(\.\d+)?$/.test(t)) return Number.NaN;
  return Number(t);
}
